import { useEffect, useMemo, useState } from 'react';
import * as ss58 from '@chainflip/utils/ss58';
import classNames from 'classnames';

export const useAccountIdInputControls = (defaultAccountId?: string) => {
  const [accountId, setAccountId] = useState(defaultAccountId ?? '');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setError(undefined);
  }, [accountId]);

  const hasError = useMemo(() => {
    if (error) return true;
    if (accountId === '') return false;

    try {
      ss58.decode(accountId);
      return false;
    } catch {
      return true;
    }
  }, [accountId, error]);

  return {
    accountId,
    setAccountId,
    hasError,
    setError,
    error,
    validatedId: !hasError && accountId !== '' ? (accountId as `cF${string}`) : null,
  };
};

export default function AccountIdInput({
  accountId,
  setAccountId,
  hasError,
  error,
}: ReturnType<typeof useAccountIdInputControls>) {
  return (
    <div className="relative flex flex-col">
      <input
        placeholder="cF..."
        type="text"
        className={classNames(
          'rounded-md border border-cf-gray-4 bg-cf-gray-2 px-3 py-2 text-14 text-white outline-none transition placeholder:text-cf-light-2 hover:bg-cf-gray-3 disabled:cursor-not-allowed disabled:hover:bg-cf-gray-2',
          hasError && 'border-cf-red-1',
        )}
        value={accountId}
        onChange={(e) => setAccountId(e.target.value)}
        spellCheck={false}
      />
      {hasError && (
        <span className="absolute -bottom-6 text-12 text-cf-red-1">
          {error ?? 'Enter a valid account ID'}
        </span>
      )}
    </div>
  );
}
